import { useEffect, useState, useCallback } from "react";
import { Video } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export function CameraMapping() {
  const [consoles, setConsoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const [c, l] = await Promise.all([
        api.get("/cameras/consoles"),
        api.get("/admin/locations"),
      ]);
      setConfigured(c.data.configured);
      setConsoles(c.data.consoles || []);
      setLocations(l.data.locations || []);
    } catch (e) {
      setConfigured(true); setLoadError(e.userMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (cid, patch) => {
    const cur = consoles.find((x) => x.console_id === cid);
    const next = { console_id: cid, location: cur.location, enabled: cur.enabled, ...patch };
    setConsoles((prev) => prev.map((x) => (x.console_id === cid ? { ...x, ...patch } : x)));
    setSaving(cid);
    try {
      await api.put("/cameras/console-map", next);
      toast.success("Camera mapping saved");
    } catch {
      toast.error("Could not save mapping");
      load();
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="text-sm text-brand-muted">Loading consoles…</div>;
  if (loadError) return <div className="text-sm text-brand-neg" data-testid="camera-mapping-error">UniFi consoles couldn't load: {loadError}</div>;
  if (!configured) return <div className="text-sm text-brand-muted">No UniFi API key saved yet — cameras are off.</div>;

  return (
    <div className="bg-brand-surface border border-brand-border rounded-xl p-6" data-testid="camera-mapping">
      <div className="flex items-center gap-2 mb-1">
        <Video className="h-4.5 w-4.5 text-brand-ink" />
        <h3 className="text-brand-ink font-medium">UniFi Camera Consoles</h3>
      </div>
      <p className="text-[11px] text-brand-muted mb-5 max-w-2xl">
        Map each Bond UniFi console to a hub location so its cameras appear on that location's dashboard.
        Uncheck "Show" to hide a console's cameras entirely.
      </p>
      <div className="space-y-2">
        {consoles.map((c) => (
          <div key={c.console_id} data-testid={`camera-console-${c.console_id}`}
            className="flex flex-wrap items-center gap-3 border border-brand-border rounded-lg px-4 py-3">
            <div className="flex-1 min-w-[180px]">
              <p className="text-sm text-brand-ink">{c.name}</p>
              {!c.owner && <span className="text-[10px] text-amber-600">not owner — snapshots may fail</span>}
            </div>
            <select value={c.location || ""} onChange={(e) => save(c.console_id, { location: e.target.value })}
              data-testid={`camera-loc-${c.console_id}`}
              className="text-sm border border-brand-border rounded-md px-2 py-1.5 bg-brand-page text-brand-ink">
              <option value="">— Unmapped —</option>
              {locations.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-brand-muted cursor-pointer">
              <input type="checkbox" checked={c.enabled !== false}
                onChange={(e) => save(c.console_id, { enabled: e.target.checked })}
                data-testid={`camera-enabled-${c.console_id}`} />
              Show
            </label>
            {saving === c.console_id && <span className="text-[10px] text-brand-tertiary">saving…</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
